import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { DocumenteEdocs } from '@/ui/documente-edocs'
import { Badge, Card, EmptyState, Skeleton, Suma } from '@/ui/primitives'
import { cursDeReferinta, eDatorie, pereche, perecheDinLei, type ContextCurs } from '@/lib/money'
import { CardZbor } from './zbor'
import { citesteMod } from '@/ui/curs-selector'

type Rez = {
  id: number; number: string; partner: string | null; booking_customer: string | null
  group_id?: number | null; group_name?: string | null
  status: string | null; begin_date: string | null; end_date: string | null
  destination_display: string | null; main_destination: string | null; service: string | null
  responsible: string | null; total_value: number | null; cost_value: number | null
  currency: string | null; suppliers: string | null; room_type: string | null
  meal_type: string | null; adults: number | null; children: number | null
  hotel: string | null; remarks: string | null
}
type FacturaMica = {
  id: number; number: string; total_value: number; currency: string
  restant_ron: number; due_date: string | null; issue_date: string | null
}
type Document = {
  type: string; name: string; description: string
  pdf_url: string; preview_url: string
}
type Detaliu = {
  reservation: Rez
  eur_rate: number
  facturi_sales: FacturaMica[]; facturi_purchase: FacturaMica[]; facturi_advance: FacturaMica[]
}

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'

function ListaFacturi({ titlu, facturi, tip, ctx }: {
  titlu: string; facturi: FacturaMica[]; tip: 'Sales' | 'Purchase'; ctx: ContextCurs
}) {
  if (!facturi?.length) return null
  return (
    <div>
      <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-2">{titlu}</h2>
      <Card className="overflow-hidden">
        <table className="w-full text-[14px]">
          <tbody>
            {facturi.map((f) => {
              const doc = { currency: f.currency, exchange_rate: null, invoice_type: tip }
              return (
                <tr key={f.id} className="border-t border-line first:border-0">
                  <td className="px-5 py-3">
                    <Link to={`/bani/facturi/${f.id}`} className="tnum font-medium text-accent hover:underline">
                      {f.number}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-muted tnum text-[13px]">{zi(f.issue_date)}</td>
                  <td className="px-5 py-3 text-right">
                    <Suma {...pereche(f.total_value, doc, ctx)} />
                  </td>
                  <td className="px-5 py-3 text-right w-32">
                    {eDatorie(f.restant_ron)
                      ? <Suma {...perecheDinLei(f.restant_ron, doc, ctx)} ton="muted" />
                      : <Badge tone="good">achitata</Badge>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

/** Documentele rezervarii (voucher, contract, bon de comanda, decont…) — se genereaza la
 *  cerere de backend. Se deschid ca fisier, fiindca endpoint-ul cere token. */
function Documente({ id }: { id: string }) {
  const [eroare, setEroare] = useState<string | null>(null)
  const [seDeschide, setSeDeschide] = useState<string | null>(null)
  const { data } = useQuery({
    queryKey: ['documente', id],
    queryFn: () => api.get<{ documents: Document[] }>(`/documents/${id}/documents/available`),
    retry: false,
  })
  const documente = data?.documents ?? []
  if (!documente.length) return null

  const deschide = async (d: Document) => {
    setEroare(null); setSeDeschide(d.type)
    try { await api.deschideFisier(d.pdf_url.replace(/^\/api/, '')) }
    catch (e) { setEroare(e instanceof Error ? e.message : 'Nu am putut deschide documentul') }
    finally { setSeDeschide(null) }
  }

  return (
    <div>
      <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-2">Documente</h2>
      <Card className="p-4">
        <div className="flex flex-wrap gap-2">
          {documente.map((d) => (
            <button key={d.type} onClick={() => deschide(d)} disabled={seDeschide === d.type}
              title={d.description}
              className="h-9 px-3.5 rounded-[var(--radius-control)] border border-line-strong text-[13.5px] hover:bg-accent-soft hover:border-accent transition-colors disabled:opacity-50">
              {seDeschide === d.type ? 'Se genereaza…' : d.name}
            </button>
          ))}
        </div>
        {eroare && <p className="mt-3 text-[13px] text-bad">{eroare}</p>}
      </Card>
    </div>
  )
}

export function ReservationDetail() {
  const { id } = useParams()
  const { data, isPending, isError } = useQuery({
    queryKey: ['rezervare', id],
    queryFn: () => api.get<Detaliu>(`/reservations/${id}`),
  })

  if (isPending) return (<><PageHeader title="Rezervare" /><div className="px-8 py-6"><Card><Skeleton /></Card></div></>)
  // API-ul raspunde 200 cu {error: …} pentru un id inexistent, nu 404 — fara verificarea
  // asta ecranul ramanea alb (verificat pe /rezervari/5541, care e numar, nu id).
  if (isError || !data?.reservation) return (<><PageHeader title="Rezervare" /><div className="px-8 py-6"><Card><EmptyState title="Nu am gasit rezervarea" hint={`Verifica adresa: ${id} nu e o rezervare din baza.`} /></Card></div></>)

  const r = data.reservation
  const ctx: ContextCurs = { bnrPlus2: data.eur_rate, mod: citesteMod(), manual: null }
  const doc = { currency: r.currency ?? 'RON', exchange_rate: null, invoice_type: 'Sales' }
  // Costul de pe rezervare e des 0 desi exista facturi de la furnizor (verificat pe 5541:
  // cost_value 0, factura SOLVEX 2.992,96 EUR) — o marja de 100% ar fi o cifra falsa.
  // Cand lipseste, costul se ia din facturile de cumparare, in moneda rezervarii.
  const cursRez = cursDeReferinta(doc, ctx)
  const costFacturiLei = (data.facturi_purchase ?? []).reduce(
    (s, f) => s + (f.currency === 'RON' ? f.total_value : f.total_value * cursRez), 0)
  const costFacturi = (r.currency ?? 'RON') === 'RON' ? costFacturiLei : costFacturiLei / cursRez
  const costDinFacturi = !r.cost_value && costFacturi > 0
  const cost = costDinFacturi ? costFacturi : (r.cost_value ?? 0)
  const marja = (r.total_value ?? 0) - cost
  const procent = r.total_value ? (marja / r.total_value) * 100 : 0

  return (
    <>
      <PageHeader
        title={`Rezervarea ${r.number}`}
        count={r.status ?? undefined}
        actions={
          <>
            <Link to={`/rezervari/${r.id}/program`}
              className="inline-flex items-center h-9 px-3.5 rounded-[var(--radius-control)] border border-line-strong text-[14px] hover:bg-accent-soft hover:border-accent">
              Program calatorie
            </Link>
            <Link to="/rezervari" className="text-[14px] text-muted hover:text-ink">← Toate rezervarile</Link>
          </>
        }
      />
      <div className="px-8 py-6 grid gap-6 lg:grid-cols-[1fr_320px] items-start">
        <div className="space-y-6 min-w-0">
          <Card className="p-5">
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              <div>
                <p className="text-[12.5px] text-muted">Client</p>
                <p className="font-medium text-[15px]">{r.partner ?? '—'}</p>
                {r.booking_customer && r.booking_customer !== r.partner && (
                  <p className="text-[13px] text-muted">titular: {r.booking_customer}</p>
                )}
              </div>
              <div>
                <p className="text-[12.5px] text-muted">Perioada</p>
                <p className="text-[14.5px]">{zi(r.begin_date)} – {zi(r.end_date)}</p>
              </div>
              <div>
                <p className="text-[12.5px] text-muted">Turisti</p>
                <p className="text-[14.5px] tnum">
                  {r.adults ?? 0} adulti{r.children ? `, ${r.children} copii` : ''}
                </p>
              </div>
              {r.responsible && (
                <div>
                  <p className="text-[12.5px] text-muted">Responsabil</p>
                  <p className="text-[14.5px]">{r.responsible}</p>
                </div>
              )}
              {r.group_id && (
                <div>
                  <p className="text-[12.5px] text-muted">Grup</p>
                  <Link to={`/rezervari/grupuri/${r.group_id}`}
                    className="text-[14.5px] text-accent-ink hover:underline">
                    {r.group_name ?? `grupul #${r.group_id}`}
                  </Link>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-line space-y-1">
              <p className="text-[14.5px]">{r.service ?? '—'}</p>
              <p className="text-[13.5px] text-muted">
                {[r.hotel, r.destination_display ?? r.main_destination, r.room_type, r.meal_type]
                  .filter(Boolean).join(' · ') || '—'}
              </p>
              {r.suppliers && <p className="text-[13px] text-muted">furnizori: {r.suppliers}</p>}
              {r.remarks && <p className="text-[13px] text-muted italic mt-2">{r.remarks}</p>}
            </div>
          </Card>

          <CardZbor reservationId={r.id} />

          <ListaFacturi titlu="Facturi de vanzare" facturi={data.facturi_sales} tip="Sales" ctx={ctx} />
          <ListaFacturi titlu="Avansuri" facturi={data.facturi_advance} tip="Sales" ctx={ctx} />
          <ListaFacturi titlu="Facturi de la furnizori" facturi={data.facturi_purchase} tip="Purchase" ctx={ctx} />
          {id && <Documente id={id} />}

          <DocumenteEdocs tip="reservation" id={r.id} />
        </div>

        <Card className="p-5 space-y-3">
          <div className="flex justify-between items-baseline">
            <span className="text-[13px] text-muted">Vanzare</span>
            <Suma {...pereche(r.total_value ?? 0, doc, ctx)} ton="strong" />
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-[13px] text-muted">
              Cost
              {costDinFacturi && <span className="block text-[11.5px]">din facturile furnizorilor</span>}
            </span>
            <Suma {...pereche(cost, doc, ctx)} ton="muted" />
          </div>
          <div className="pt-3 border-t border-line flex justify-between items-baseline">
            <span className="text-[13px] text-muted">Marja</span>
            <span className="text-right">
              <Suma {...pereche(marja, doc, ctx)} ton="strong" />
              <span className={'block text-[12px] tnum ' + (procent < 0 ? 'text-bad' : 'text-good')}>
                {procent.toFixed(1)}%
              </span>
            </span>
          </div>
        </Card>
      </div>
    </>
  )
}
